import { useEffect, useState } from 'react';
import { useAuthStore } from '../store/authStore';
import { useTranslation } from '../hooks/useTranslation';
import { showTelegramAlert } from '../services/telegramService';
import { copyTradeApi } from '../services/copyTradeApi';
import PageSkeleton from '../components/skeletons/PageSkeleton';
import { useTelegramTheme, getInputClassesLarge } from '../hooks/useTelegramTheme';

interface CopyTradeBot {
  id: string;
  name: string;
  nameVi: string;
  image: string;
  link: string;
  exchange?: string;
  description?: string;
  descriptionVi?: string;
  order: number;
}

interface ExchangeGroup {
  exchange: string;
  exchangeName: string;
  bots: CopyTradeBot[];
}

// Helper function to normalize exchange name to exchange code
const normalizeExchange = (exchangeName: string): string => {
  if (!exchangeName) return '';
  const lower = exchangeName.toLowerCase();
  
  if (lower.includes('bybit')) return 'bybit';
  if (lower.includes('binance')) return 'binance';
  if (lower.includes('okx')) return 'okx';
  if (lower.includes('mexc')) return 'mexc';
  if (lower.includes('gate')) return 'gate';
  if (lower.includes('vantage')) return 'vantage';
  
  return lower;
};

const LinkCopyTrade = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const theme = useTelegramTheme();
  const [bots, setBots] = useState<CopyTradeBot[]>([]);
  const [exchangeGroups, setExchangeGroups] = useState<ExchangeGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedBot, setSelectedBot] = useState<CopyTradeBot | null>(null);
  const [uid, setUid] = useState('');
  const [copyTradeAccounts, setCopyTradeAccounts] = useState<any[]>([]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    if (!user) return;
    
    try {
      const [botsData, accountsData] = await Promise.all([
        copyTradeApi.getBots(),
        copyTradeApi.getAccounts()
      ]);
      
      setBots(botsData);
      setCopyTradeAccounts(accountsData);
      
      // Group bots by exchange
      const groupsMap = new Map<string, CopyTradeBot[]>();
      
      botsData.forEach((bot: CopyTradeBot) => {
        const exchangeCode = normalizeExchange(bot.exchange || '');
        if (exchangeCode) {
          if (!groupsMap.has(exchangeCode)) {
            groupsMap.set(exchangeCode, []);
          }
          groupsMap.get(exchangeCode)!.push(bot);
        }
      });
      
      // Convert to array and sort
      const groups: ExchangeGroup[] = Array.from(groupsMap.entries()).map(([exchange, bots]) => {
        // Get exchange name from first bot
        const exchangeName = bots[0]?.exchange || exchange.toUpperCase();
        return {
          exchange,
          exchangeName,
          bots: bots.sort((a, b) => a.order - b.order),
        };
      });
      
      setExchangeGroups(groups);
      
      // Auto-select first bot if available
      if (botsData.length > 0 && !selectedBot) {
        setSelectedBot(botsData[0]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkAccount = async () => {
    // Check email first
    if (!user?.email || user.email.trim() === '') {
      showTelegramAlert(t('settings.emailVerification'));
      return;
    }

    if (!uid.trim()) {
      showTelegramAlert(t('copyTrade.pleaseEnterUid'));
      return;
    }

    if (!selectedBot) {
      showTelegramAlert(t('copyTrade.pleaseSelectBot'));
      return;
    }

    try {
      await copyTradeApi.linkAccount(selectedBot.id, uid.trim());
      showTelegramAlert(t('copyTrade.submittedSuccessfully'));
      setUid('');
      // Reload accounts to get updated status
      await loadData();
      // Navigate back to Exchanges page
      window.history.back();
    } catch (error: any) {
      showTelegramAlert(error.message || 'Error linking account');
    }
  };

  const handleOpenCopyTradeLink = (bot: CopyTradeBot) => {
    if (bot.link) {
      window.open(bot.link, '_blank');
    }
  };

  // Check if user has pending or active account for selected bot
  const getAccountStatusForBot = (botId: string) => {
    const accountsForBot = copyTradeAccounts.filter(acc => acc.botId === botId);
    
    if (accountsForBot.length === 0) return null;
    
    // Check for active first, then pending, then rejected
    const activeAccount = accountsForBot.find(acc => acc.status === 'active');
    if (activeAccount) return { status: 'active', account: activeAccount };
    
    const pendingAccount = accountsForBot.find(acc => acc.status === 'pending');
    if (pendingAccount) return { status: 'pending', account: pendingAccount };
    
    const rejectedAccount = accountsForBot.find(acc => acc.status === 'rejected');
    if (rejectedAccount) return { status: 'rejected', account: rejectedAccount };
    
    return null;
  };

  // Check if form should be disabled
  const shouldDisableForm = () => {
    if (!selectedBot) return false;
    const accountStatus = getAccountStatusForBot(selectedBot.id);
    
    // Disable if active, pending, or disabled
    if (!accountStatus) return false;
    return accountStatus.status === 'active' || 
           accountStatus.status === 'pending' || 
           accountStatus.account.disabled === true;
  };

  const getAccountStatusInfo = () => {
    if (!selectedBot) return null;
    return getAccountStatusForBot(selectedBot.id);
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (bots.length === 0) {
    return (
      <div className="p-4 pb-24">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {t('copyTrade.title')}
              </h1>
              <p className="text-xs text-gray-400">
                {t('copyTrade.notConfigured')}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-12 text-center border border-gray-700/50">
          <div className="w-20 h-20 mx-auto mb-4 bg-purple-500/10 rounded-2xl flex items-center justify-center">
            <svg className="w-12 h-12 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-white mb-2">
            {t('copyTrade.noExchangesAvailable')}
          </p>
          <p className="text-sm text-gray-400">
            {t('copyTrade.pleaseContactSupport')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-24">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-yellow-500 flex items-center justify-center shadow-lg shadow-primary/40 animate-pulse">
            <svg className="w-7 h-7 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-black bg-gradient-to-r from-primary via-yellow-400 to-primary bg-clip-text text-transparent">
              {t('copyTrade.title')}
            </h1>
            <p className="text-sm text-gray-400 flex items-center gap-1.5">
              {t('copyTrade.clickToCopyTrade')}
            </p>
          </div>
        </div>
      </div>

      <div className="relative overflow-hidden bg-gradient-to-br from-gray-800/80 to-gray-900/80 rounded-2xl p-5 mb-6 border border-gray-700/50 hover:border-primary/30 transition-all">
        <div className="relative">
          <h2 className="text-lg mb-4 text-white font-semibold flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            {t('copyTrade.linkCopyTradeAccount')}
          </h2>
          
          {/* Exchange Groups */}
          {exchangeGroups.map((group) => (
            <div key={group.exchange} className="mb-4">
              <label className="block text-sm text-gray-400 mb-2 font-medium">
                {group.exchangeName}
              </label>
              <div className="space-y-2">
                {group.bots.map((bot) => (
                  <div
                    key={bot.id}
                    className={`p-3 rounded-xl border transition-all cursor-pointer ${
                      selectedBot?.id === bot.id
                        ? 'bg-primary/10 border-primary/50'
                        : 'bg-gray-900/50 border-gray-700/50 hover:border-primary/30'
                    }`}
                    onClick={() => setSelectedBot(bot)}
                  >
                    <div className="flex items-center gap-3">
                      {bot.image && (
                        <img
                          src={bot.image}
                          alt={bot.name}
                          className="w-12 h-12 rounded-lg object-cover"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"><rect fill="%23374151" width="48" height="48"/></svg>';
                          }}
                        />
                      )}
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{bot.name}</p>
                        {bot.nameVi && bot.nameVi !== bot.name && (
                          <p className="text-gray-400 text-xs">{bot.nameVi}</p>
                        )}
                      </div>
                      {selectedBot?.id === bot.id && (
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {selectedBot && (
            <>
              {/* Show account status if exists */}
              {getAccountStatusInfo() && (
                <div className={`mb-4 p-4 rounded-xl border transition-all ${
                  getAccountStatusInfo()?.account.disabled
                    ? 'bg-gray-500/10 border-gray-500/20'
                    : getAccountStatusInfo()?.status === 'active'
                    ? 'bg-green-500/10 border-green-500/20'
                    : getAccountStatusInfo()?.status === 'pending'
                    ? 'bg-orange-500/10 border-orange-500/20'
                    : 'bg-red-500/10 border-red-500/20'
                }`}>
                  <div className="flex items-start gap-3">
                    <svg
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        getAccountStatusInfo()?.account.disabled
                          ? 'text-gray-500'
                          : getAccountStatusInfo()?.status === 'active'
                          ? 'text-green-400'
                          : getAccountStatusInfo()?.status === 'pending'
                          ? 'text-orange-400'
                          : 'text-red-400'
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                    >
                      {getAccountStatusInfo()?.account.disabled ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      ) : getAccountStatusInfo()?.status === 'pending' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : getAccountStatusInfo()?.status === 'active' ? (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      ) : (
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      )}
                    </svg>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        getAccountStatusInfo()?.account.disabled
                          ? 'text-gray-400'
                          : getAccountStatusInfo()?.status === 'active'
                          ? 'text-green-200'
                          : getAccountStatusInfo()?.status === 'pending'
                          ? 'text-orange-200'
                          : 'text-red-200'
                      }`}>
                        {getAccountStatusInfo()?.account.disabled
                          ? `${selectedBot.name} - ${t('copyTrade.disabled')}`
                          : getAccountStatusInfo()?.status === 'pending'
                          ? t('copyTrade.submittedSuccessfully')
                          : getAccountStatusInfo()?.status === 'active'
                          ? `${selectedBot.name} - ${t('linkExchange.active')}`
                          : `${selectedBot.name} - ${t('exchanges.rejectedAccounts')}`}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        UID: {getAccountStatusInfo()?.account.uid} • {new Date(getAccountStatusInfo()?.account.createdAt || '').toLocaleString()}
                      </p>
                      {getAccountStatusInfo()?.status === 'rejected' && (
                        <p className="text-xs text-red-300 mt-2">
                          {t('copyTrade.accountRejectedPleaseResubmit')}
                        </p>
                      )}
                      {getAccountStatusInfo()?.account.disabled && (
                        <p className="text-xs text-gray-400 mt-2">
                          {t('copyTrade.accountDisabledMessage')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Only show form if no active/pending account or if rejected */}
              {!shouldDisableForm() && (
                <>
                  <div className="mb-4 p-4 bg-primary/5 rounded-xl border border-primary/20 hover:border-primary/30 transition-all group">
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-sm text-gray-300 font-medium">
                        {t('copyTrade.step1')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleOpenCopyTradeLink(selectedBot)}
                      className="w-full py-3.5 bg-gradient-to-r from-primary to-yellow-500 text-black rounded-xl text-base font-bold mb-3 hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] active:scale-95 transition-all flex items-center justify-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                      </svg>
                      {t('copyTrade.openCopyTradeLink')}
                    </button>
                    <p className="text-xs text-gray-400 text-center">
                      {t('copyTrade.clickToCopyTrade')}
                    </p>
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm text-gray-400 mb-2 font-medium flex items-center gap-2">
                      <svg className="w-4 h-4 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                      {t('copyTrade.step2')}
                    </label>
                    <input
                      type="text"
                      value={uid}
                      onChange={(e) => setUid(e.target.value)}
                      placeholder={t('copyTrade.enterUidPlaceholder')}
                      className={getInputClassesLarge(theme, 'hover:border-primary/30')}
                    />
                  </div>
                </>
              )}
            </>
          )}


          <div className="flex gap-3">
            <button
              className="flex-1 py-3.5 bg-gray-800/50 hover:bg-gray-700/50 text-white rounded-xl text-base font-semibold transition-all hover:scale-[1.02] active:scale-95 border border-gray-700/50 hover:border-gray-600"
              onClick={() => window.history.back()}
            >
              {t('common.cancel')}
            </button>
            {!shouldDisableForm() && (
              <button
                className="flex-1 py-3.5 bg-gradient-to-r from-primary to-yellow-500 text-black rounded-xl text-base font-bold transition-all hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2"
                onClick={handleLinkAccount}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                {t('common.confirm')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LinkCopyTrade;
